import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { ScrollText, ChevronLeft, ChevronRight, Filter, Shield, ShieldCheck, ShieldX, LogIn, Monitor, Key, Loader2, UserCog, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const actionLabels = {
  '2fa_enabled': { label: '2FA Enabled', color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  '2fa_disabled': { label: '2FA Disabled', color: 'bg-red-100 text-red-700', icon: ShieldX },
  '2fa_disabled_by_admin': { label: '2FA Disabled (Admin)', color: 'bg-orange-100 text-orange-700', icon: ShieldX },
  '2fa_reset_by_admin': { label: '2FA Reset (Admin)', color: 'bg-orange-100 text-orange-700', icon: RotateCcw },
  'login_success': { label: 'Login', color: 'bg-blue-100 text-blue-700', icon: LogIn },
  'login_2fa_success': { label: 'Login (2FA)', color: 'bg-blue-100 text-blue-700', icon: ShieldCheck },
  'login_2fa_required': { label: '2FA Prompted', color: 'bg-yellow-100 text-yellow-700', icon: Shield },
  'login_trusted_device': { label: 'Login (Trusted)', color: 'bg-indigo-100 text-indigo-700', icon: Monitor },
  '2fa_verify_failed': { label: '2FA Failed', color: 'bg-red-100 text-red-700', icon: ShieldX },
  'trusted_device_added': { label: 'Device Trusted', color: 'bg-green-100 text-green-700', icon: Monitor },
  'trusted_device_revoked': { label: 'Device Revoked', color: 'bg-gray-100 text-gray-700', icon: Monitor },
  'all_trusted_devices_revoked': { label: 'All Devices Revoked', color: 'bg-gray-100 text-gray-700', icon: Monitor },
  'recovery_code_used': { label: 'Recovery Code Used', color: 'bg-orange-100 text-orange-700', icon: Key },
  'recovery_codes_regenerated': { label: 'Codes Regenerated', color: 'bg-blue-100 text-blue-700', icon: Key },
  'email_verified': { label: 'Email Verified', color: 'bg-green-100 text-green-700', icon: ShieldCheck },
  'admin_update_user': { label: 'User Updated', color: 'bg-blue-100 text-blue-700', icon: UserCog },
  'log_retention_updated': { label: 'Retention Updated', color: 'bg-purple-100 text-purple-700', icon: ScrollText },
  'log_cleanup_executed': { label: 'Log Cleanup', color: 'bg-gray-100 text-gray-600', icon: ScrollText },
};

const AdminAuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterLogType, setFilterLogType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filterAction) params.action = filterAction;
      if (filterLogType) params.log_type = filterLogType;
      const res = await api.get('/admin/2fa/audit', { params });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [page, filterAction, filterLogType]);

  const getActionConfig = (action) => {
    return actionLabels[action] || { label: action, color: 'bg-gray-100 text-gray-600', icon: ScrollText };
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterLogType('');
    setPage(1);
  };

  const hasFilters = filterAction || filterLogType;

  return (
    <div className="p-6 space-y-6" data-testid="admin-audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>Security Audit Log</h1>
          <p className="text-gray-500 text-sm mt-1">{total} events logged</p>
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 ${showFilters ? 'border-[#1A73E8] text-[#1A73E8]' : ''}`} data-testid="toggle-audit-filters">
          <Filter className="w-4 h-4" /> Filter {hasFilters && <span className="w-2 h-2 bg-[#1A73E8] rounded-full" />}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Action:</label>
            <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-action-select">
              <option value="">All Actions</option>
              <option value="2fa_enabled">2FA Enabled</option>
              <option value="2fa_disabled">2FA Disabled</option>
              <option value="2fa_reset_by_admin">2FA Reset by Admin</option>
              <option value="login">Login Events</option>
              <option value="trusted_device">Trusted Device Events</option>
              <option value="recovery">Recovery Code Events</option>
              <option value="email_verified">Email Verification</option>
              <option value="log_retention">Log Retention Events</option>
              <option value="log_cleanup">Log Cleanup Events</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Log Type:</label>
            <select value={filterLogType} onChange={e => { setFilterLogType(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-log-type-select">
              <option value="">All Types</option>
              <option value="security">Security</option>
              <option value="user">User Activity</option>
            </select>
          </div>
          {hasFilters && <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700 underline" data-testid="clear-filters-btn">Clear all</button>}
        </div>
      )}

      <div className="bg-white rounded-xl border">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1A73E8]" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ScrollText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No audit events found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-sm text-gray-600">
                    <th className="px-5 py-3">Timestamp</th>
                    <th className="px-5 py-3">Actor</th>
                    <th className="px-5 py-3">Target</th>
                    <th className="px-5 py-3">Action</th>
                    <th className="px-5 py-3">Details</th>
                    <th className="px-5 py-3">IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const config = getActionConfig(log.action);
                    const Icon = config.icon;
                    const isSelfAction = log.actor_email === log.target_email;
                    return (
                      <tr key={log.id || i} className="border-b last:border-0 hover:bg-gray-50" data-testid={`audit-log-row-${i}`}>
                        <td className="px-5 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {new Date(log.created_at).toLocaleDateString()}<br />
                          <span className="text-gray-400">{new Date(log.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit', second:'2-digit'})}</span>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-sm text-gray-900 flex items-center gap-1">
                            {log.actor_name || 'System'}
                            {log.actor_is_admin && <span className="text-[9px] bg-[#E8F0FE] text-[#1A73E8] px-1 py-0.5 rounded font-medium ml-1">Admin</span>}
                          </p>
                          <p className="text-xs text-gray-400">{log.actor_email || '-'}</p>
                        </td>
                        <td className="px-5 py-3">
                          {isSelfAction ? (
                            <span className="text-xs text-gray-400 italic">Self</span>
                          ) : (
                            <>
                              <p className="text-sm text-gray-900">{log.target_name || '-'}</p>
                              <p className="text-xs text-gray-400">{log.target_email || '-'}</p>
                            </>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
                            <Icon className="w-3 h-3" /> {config.label}
                          </span>
                          {log.log_type && (
                            <p className="text-[10px] text-gray-400 mt-1 capitalize">{log.log_type}</p>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-500 max-w-[200px]">
                          {log.details && Object.keys(log.details).length > 0 && (
                            <span className="truncate block" title={JSON.stringify(log.details)}>
                              {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ').slice(0, 80)}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{log.ip_address || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t">
                <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50" data-testid="audit-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50" data-testid="audit-next-page"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogPage;
